/**
 * 3D Model Management Service
 * Handles CRUD for property_3d_models and model_performance_metrics tables.
 */

import { supabase } from '../lib/supabase';
import { resolveAssetUrl, cacheAssetUrl, getCacheKey, prefetchAssets } from './assetCacheService';

const BUCKET = 'property-assets';

// ─── Model CRUD ───────────────────────────────────────────────────────────────

/**
 * Fetch all 3D models, optionally filtered by property.
 */
export const fetchModels = async ({ propertyId = null, visibility = null } = {}) => {
  let query = supabase?.from('property_3d_models')?.select(`
      *,
      properties:property_id (id, title, commune_id)
    `)?.order('created_at', { ascending: false });

  if (propertyId) query = query?.eq('property_id', propertyId);
  if (visibility) query = query?.eq('visibility', visibility);

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

/**
 * Fetch a single model by ID.
 */
export const fetchModelById = async (modelId) => {
  const { data, error } = await supabase?.from('property_3d_models')?.select(`*, properties:property_id (id, title, commune_id)`)?.eq('id', modelId)?.single();
  if (error) throw error;
  return data;
};

/**
 * Create a new 3D model record.
 */
export const createModel = async (modelData) => {
  const { data, error } = await supabase?.from('property_3d_models')?.insert([{
      property_id: modelData?.propertyId,
      name: modelData?.name,
      description: modelData?.description || null,
      file_type: modelData?.fileType || 'glb',
      storage_path: modelData?.storagePath,
      public_url: modelData?.publicUrl || null,
      file_size_bytes: modelData?.fileSizeBytes || 0,
      visibility: modelData?.visibility || 'visible',
      display_order: modelData?.displayOrder || 0,
      is_primary: modelData?.isPrimary || false,
      tags: modelData?.tags || [],
      metadata: modelData?.metadata || {},
      uploaded_by: modelData?.uploadedBy || null,
    }])?.select()?.single();
  if (error) throw error;
  return data;
};

/**
 * Update an existing model record.
 */
export const updateModel = async (modelId, updates) => {
  const payload = {};
  if (updates?.name !== undefined) payload.name = updates?.name;
  if (updates?.description !== undefined) payload.description = updates?.description;
  if (updates?.visibility !== undefined) payload.visibility = updates?.visibility;
  if (updates?.displayOrder !== undefined) payload.display_order = updates?.displayOrder;
  if (updates?.isPrimary !== undefined) payload.is_primary = updates?.isPrimary;
  if (updates?.tags !== undefined) payload.tags = updates?.tags;
  if (updates?.propertyId !== undefined) payload.property_id = updates?.propertyId;
  if (updates?.metadata !== undefined) payload.metadata = updates?.metadata;

  const { data, error } = await supabase?.from('property_3d_models')?.update(payload)?.eq('id', modelId)?.select()?.single();
  if (error) throw error;
  return data;
};

/**
 * Delete a model record (and optionally its storage file).
 */
export const deleteModel = async (modelId, storagePath = null) => {
  if (storagePath) {
    await supabase?.storage?.from(BUCKET)?.remove([storagePath]);
  }
  const { error } = await supabase?.from('property_3d_models')?.delete()?.eq('id', modelId);
  if (error) throw error;
};

/**
 * Toggle visibility of a model.
 */
export const toggleModelVisibility = async (modelId, currentVisibility) => {
  const next = currentVisibility === 'visible' ? 'hidden' : 'visible';
  return updateModel(modelId, { visibility: next });
};

/**
 * Set a model as primary for its property (unsets others).
 */
export const setPrimaryModel = async (modelId, propertyId) => {
  // Unset all primaries for this property
  await supabase?.from('property_3d_models')?.update({ is_primary: false })?.eq('property_id', propertyId);

  // Set the selected one
  return updateModel(modelId, { isPrimary: true });
};

/**
 * Upload a 3D model file to Supabase Storage and create a DB record.
 */
export const uploadModelFile = async (file, propertyId, modelName, uploadedBy = null, onProgress = null) => {
  const ext = file?.name?.split('.')?.pop()?.toLowerCase();
  const sanitized = file?.name?.normalize('NFD')?.replace(/[\u0300-\u036f]/g, '')?.replace(/[^a-zA-Z0-9._-]/g, '_');
  const storagePath = `3d-models/${propertyId}/${Date.now()}_${sanitized}`;

  const mimeMap = {
    glb: 'model/gltf-binary',
    gltf: 'model/gltf+json',
    obj: 'model/obj',
  };

  onProgress?.(20);

  const { error: uploadError } = await supabase?.storage?.from(BUCKET)?.upload(storagePath, file, {
      contentType: mimeMap?.[ext] || 'application/octet-stream',
      upsert: false,
    });

  if (uploadError) throw uploadError;
  onProgress?.(70);

  const { data: urlData } = supabase?.storage?.from(BUCKET)?.getPublicUrl(storagePath);
  const publicUrl = urlData?.publicUrl;

  // Cache the resolved URL immediately after upload
  if (publicUrl) {
    const key = getCacheKey(storagePath);
    cacheAssetUrl(key, publicUrl, { fileType: ext, fileSizeBytes: file?.size });
  }

  onProgress?.(85);

  const record = await createModel({
    propertyId,
    name: modelName || file?.name,
    fileType: ext,
    storagePath,
    publicUrl,
    fileSizeBytes: file?.size,
    uploadedBy,
  });

  onProgress?.(100);
  return record;
};

// ─── Performance Metrics ──────────────────────────────────────────────────────

/**
 * Fetch aggregated performance metrics for all models.
 */
export const fetchModelMetrics = async ({ propertyId = null, modelId = null, days = 30 } = {}) => {
  const since = new Date();
  since?.setDate(since?.getDate() - days);

  let query = supabase?.from('model_performance_metrics')?.select(`
      *,
      model:model_id (id, name, file_type, property_id, visibility),
      property:property_id (id, title)
    `)?.gte('date_recorded', since?.toISOString()?.split('T')?.[0])?.order('date_recorded', { ascending: false });

  if (propertyId) query = query?.eq('property_id', propertyId);
  if (modelId) query = query?.eq('model_id', modelId);

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

/**
 * Fetch aggregated summary stats per model.
 */
export const fetchModelMetricsSummary = async () => {
  const { data, error } = await supabase?.from('model_performance_metrics')?.select(`
      model_id,
      model:model_id (id, name, file_type, visibility, property_id),
      view_count,
      unique_viewers,
      avg_view_duration_seconds,
      interaction_count,
      load_time_ms,
      error_count,
      last_viewed_at
    `);

  if (error) throw error;

  // Aggregate by model_id
  const summary = {};
  for (const row of data || []) {
    const mid = row?.model_id;
    if (!summary?.[mid]) {
      summary[mid] = {
        modelId: mid,
        model: row?.model,
        totalViews: 0,
        totalUniqueViewers: 0,
        avgViewDuration: 0,
        totalInteractions: 0,
        avgLoadTime: 0,
        totalErrors: 0,
        lastViewedAt: null,
        recordCount: 0,
      };
    }
    const s = summary?.[mid];
    s.totalViews += row?.view_count || 0;
    s.totalUniqueViewers += row?.unique_viewers || 0;
    s.totalInteractions += row?.interaction_count || 0;
    s.totalErrors += row?.error_count || 0;
    s.avgViewDuration = (s?.avgViewDuration * s?.recordCount + (row?.avg_view_duration_seconds || 0)) / (s?.recordCount + 1);
    s.avgLoadTime = (s?.avgLoadTime * s?.recordCount + (row?.load_time_ms || 0)) / (s?.recordCount + 1);
    s.recordCount += 1;
    if (!s?.lastViewedAt || (row?.last_viewed_at && row?.last_viewed_at > s?.lastViewedAt)) {
      s.lastViewedAt = row?.last_viewed_at;
    }
  }

  return Object.values(summary);
};

/**
 * Fetch properties list for assignment dropdown.
 */
export const fetchPropertiesForAssignment = async () => {
  const { data, error } = await supabase?.from('properties')?.select('id, title, commune_id, status')?.order('title', { ascending: true });
  if (error) throw error;
  return data || [];
};

/**
 * Fetch all 3D models for a property and prefetch their URLs into cache.
 * Optimized for mobile and variable Abidjan connectivity.
 */
export const fetchAndPrefetchModels = async ({ propertyId = null, visibility = null } = {}) => {
  const models = await fetchModels({ propertyId, visibility });

  // Collect all public URLs and prefetch them into cache
  const urls = models
    ?.filter((m) => m?.public_url)
    ?.map((m) => m?.public_url);

  if (urls?.length > 0) {
    prefetchAssets(urls, 300); // 300ms stagger to avoid overwhelming slow connections
  }

  return models;
};

/**
 * Resolves the cached or fresh public URL for a model's storage path.
 * @param {string} storagePath - Storage path or full URL
 * @returns {string|null}
 */
export const resolveModelUrl = (storagePath) => {
  if (!storagePath) return null;
  return resolveAssetUrl(storagePath, (path) => {
    const { data } = supabase?.storage?.from(BUCKET)?.getPublicUrl(path);
    return data?.publicUrl || null;
  });
};

export default {
  fetchModels,
  fetchModelById,
  createModel,
  updateModel,
  deleteModel,
  toggleModelVisibility,
  setPrimaryModel,
  uploadModelFile,
  fetchAndPrefetchModels,
  resolveModelUrl,
  fetchModelMetrics,
  fetchModelMetricsSummary,
  fetchPropertiesForAssignment,
};
